/**
 * Data export — CSV and JSON export of visible events, analysis results,
 * CII scores, and dashboard state. Respects current time range filter.
 *
 * Export types:
 * - Events: all visible events (respects time filter + active layers)
 * - CII Scores: current Country Instability Index data
 * - Analysis: combined intelligence analysis results
 * - Full State: events + analysis + CII in a single JSON file
 */

import { appStore } from '@/core/store';
import { filterEventsByTimeRange } from '@/utils/time-filter';
import { getAnalysisEngine } from '@/intelligence/analysis/engine';
import type { NormalizedEvent } from '@/sources/base-source';
import type { AnalysisResult, CIIScore } from '@/intelligence/analysis/types';

export type ExportFormat = 'csv' | 'json';
export type ExportScope = 'events' | 'cii' | 'analysis' | 'full';

/** Escape a field for RFC 4180 CSV. */
function escapeCSV(value: unknown): string {
  const str = String(value ?? '');
  if (str.includes('"') || str.includes(',') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/** Trigger a file download from a Blob. */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  // Defer revocation — immediate revoke can race with the browser's download start
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Get a timestamp string for filenames (YYYYMMDD-HHMMSS). */
function fileTimestamp(): string {
  const now = new Date();
  const y = now.getUTCFullYear();
  const mo = String(now.getUTCMonth() + 1).padStart(2, '0');
  const d = String(now.getUTCDate()).padStart(2, '0');
  const h = String(now.getUTCHours()).padStart(2, '0');
  const mi = String(now.getUTCMinutes()).padStart(2, '0');
  const s = String(now.getUTCSeconds()).padStart(2, '0');
  return `${y}${mo}${d}-${h}${mi}${s}`;
}

/** Get filtered events based on current time range. */
function getFilteredEvents(): NormalizedEvent[] {
  const state = appStore.getState();
  return filterEventsByTimeRange(state.events, state.timeRange);
}

/** Export events as CSV. */
function exportEventsCSV(events: NormalizedEvent[]): void {
  const headers = [
    'id', 'source', 'timestamp', 'type', 'severity',
    'title', 'description', 'lat', 'lng',
    'country', 'countryCode', 'confidence', 'tags',
  ];

  const rows = events.map(e => [
    escapeCSV(e.id),
    escapeCSV(e.source),
    escapeCSV(e.timestamp),
    escapeCSV(e.type),
    escapeCSV(e.severity ?? ''),
    escapeCSV(e.title),
    escapeCSV(e.description ?? ''),
    escapeCSV(e.location?.lat ?? ''),
    escapeCSV(e.location?.lng ?? ''),
    escapeCSV(e.location?.country ?? ''),
    escapeCSV(e.location?.countryCode ?? ''),
    escapeCSV(e.confidence ?? ''),
    escapeCSV(e.tags?.join('; ') ?? ''),
  ].join(','));

  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  downloadBlob(blob, `sentinel-events-${fileTimestamp()}.csv`);
}

/** Export events as JSON. */
function exportEventsJSON(events: NormalizedEvent[]): void {
  const data = events.map(e => ({
    id: e.id,
    source: e.source,
    timestamp: e.timestamp,
    type: e.type,
    severity: e.severity ?? null,
    title: e.title,
    description: e.description ?? null,
    location: e.location ?? null,
    confidence: e.confidence ?? null,
    tags: e.tags ?? [],
  }));
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  downloadBlob(blob, `sentinel-events-${fileTimestamp()}.json`);
}

/** Export CII scores as CSV. */
function exportCIICSV(scores: Map<string, CIIScore>): void {
  const headers = [
    'countryCode', 'countryName', 'overall',
    'unrest', 'conflict', 'security', 'information',
    'trend', 'lastUpdated',
  ];

  const rows: string[] = [];
  for (const score of scores.values()) {
    rows.push([
      escapeCSV(score.countryCode),
      escapeCSV(score.countryName),
      escapeCSV(score.overall),
      escapeCSV(score.components.unrest),
      escapeCSV(score.components.conflict),
      escapeCSV(score.components.security),
      escapeCSV(score.components.information),
      escapeCSV(score.trend),
      escapeCSV(score.lastUpdated),
    ].join(','));
  }

  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  downloadBlob(blob, `sentinel-cii-${fileTimestamp()}.csv`);
}

/** Export CII scores as JSON. */
function exportCIIJSON(scores: Map<string, CIIScore>): void {
  const data = Array.from(scores.values());
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  downloadBlob(blob, `sentinel-cii-${fileTimestamp()}.json`);
}

/** Serialize an AnalysisResult to a JSON-safe format (converting Maps). */
function serializeAnalysisResult(result: AnalysisResult): Record<string, unknown> {
  return {
    timestamp: result.timestamp,
    focalPoints: result.focalPoints,
    surges: result.surges,
    convergences: result.convergences,
    ciiScores: Array.from(result.ciiScores.values()),
    correlations: result.correlations,
    hotspotEscalations: result.hotspotEscalations,
    cascades: result.cascades,
    compositeRisks: result.compositeRisks,
    aggregatedSignals: result.aggregatedSignals,
    velocity: result.velocity ? {
      ...result.velocity,
      perEntity: Array.from(result.velocity.perEntity.entries()).map(
        ([key, val]) => ({ entity: key, ...val }),
      ),
    } : null,
    trendingKeywords: result.trendingKeywords,
    breakingNews: result.breakingNews,
    sentimentScores: result.sentimentScores,
    aisAnomalies: result.aisAnomalies,
  };
}

/** Export analysis results as JSON. */
function exportAnalysisJSON(result: AnalysisResult): void {
  const data = serializeAnalysisResult(result);
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  downloadBlob(blob, `sentinel-analysis-${fileTimestamp()}.json`);
}

/**
 * Export data based on scope and format.
 * Returns true if export succeeded, false if no data available.
 */
export function exportData(scope: ExportScope, format: ExportFormat): boolean {
  try {
    const engine = getAnalysisEngine();
    const result = engine?.getLastResult();

    switch (scope) {
      case 'events': {
        const events = getFilteredEvents();
        if (events.length === 0) return false;
        if (format === 'csv') exportEventsCSV(events);
        else exportEventsJSON(events);
        return true;
      }

      case 'cii': {
        const scores = result?.ciiScores;
        if (!scores || scores.size === 0) return false;
        if (format === 'csv') exportCIICSV(scores);
        else exportCIIJSON(scores);
        return true;
      }

      case 'analysis': {
        if (!result) return false;
        // Analysis results are complex — JSON only
        exportAnalysisJSON(result);
        return true;
      }

      case 'full': {
        const events = getFilteredEvents();
        const fullData: Record<string, unknown> = {
          exportedAt: new Date().toISOString(),
          variant: appStore.getState().variant,
          timeRange: appStore.getState().timeRange,
          eventCount: events.length,
          events: events.map(e => ({
            id: e.id,
            source: e.source,
            timestamp: e.timestamp,
            type: e.type,
            severity: e.severity ?? null,
            title: e.title,
            location: e.location ?? null,
            tags: e.tags ?? [],
          })),
        };
        if (result) {
          fullData.analysis = serializeAnalysisResult(result);
        }
        const json = JSON.stringify(fullData, null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        downloadBlob(blob, `sentinel-full-${fileTimestamp()}.json`);
        return true;
      }

      default:
        return false;
    }
  } catch (err) {
    console.error('[Sentinel] Export failed:', err);
    return false;
  }
}
